import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FEATURE_GATE_NAMES, FEATURE_GATE_DESCRIPTIONS, featureGateResolution } from '@padcanvas/shared';
import { Modal, formatDateMonthDay, formatDateTimePrecise } from '@shared-ui/components';
import { adminApi, type FeatureGateDefinition } from '../../api/adminApi';
import './FeatureGates.css';

/** Gate names the app code actually reads (single source of truth in
 *  @padcanvas/shared). Used to derive each row's status below. */
const KNOWN_GATE_NAMES = new Set<string>(FEATURE_GATE_NAMES);

/** A gate the app resolves with no user in hand — read pre-auth, or on a path
 *  that serves everyone the same. Rings and per-user overrides are not merely
 *  unset for one, they cannot apply, so the controls for them are absent rather
 *  than disabled: a control that can only refuse teaches nothing. The server
 *  refuses them too — the UI is not the enforcement. */
const isGlobalGate = (name: string) => featureGateResolution(name) === 'global';

const GLOBAL_HINT = 'This gate is read before anyone signs in, so there is no user to '
  + 'override it for and no ring to target. It is on or off for everyone.';

const ALL_RINGS = ['devtesting', 'beta', 'prod', 'guest'] as const;
type Ring = typeof ALL_RINGS[number];

interface GateEntry {
  name: string;
  enabled: boolean;
  description: string;
  enabledForRings: Ring[];
  allowOverride: boolean;
  /** Server-stamped — absent on a gate saved before these fields existed. */
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/**
 * What this gate IS, as one value.
 *
 * The two drift cases used to live in their own section below the list, which
 * meant the thing most worth acting on — a gate the app reads that nobody has
 * configured, silently resolving to off — was the thing you had to scroll past
 * everything else to find. They are rows now, and the status is what separates
 * them:
 *
 *   on / off        configured, and doing what it says
 *   unconfigured    app code reads it, nothing configures it → resolves OFF
 *   unused          configured here, no app code reads it → dead weight
 */
type GateStatus = 'on' | 'off' | 'unconfigured' | 'unused';

interface GateRow {
  name: string;
  description: string;
  status: GateStatus;
  /** Absent for `unconfigured` — there is no definition to edit yet. */
  entry?: GateEntry;
}

const STATUS_LABEL: Record<GateStatus, string> = {
  on: 'On',
  off: 'Off',
  unconfigured: 'Not configured',
  unused: 'Not used by app',
};

const STATUS_TITLE: Record<GateStatus, string> = {
  on: 'Enabled globally (subject to any rollout rings).',
  off: 'Configured and switched off.',
  unconfigured: 'App code reads this gate via isFeatureEnabled, but nothing configures it — '
    + 'so it resolves to its default (off). This is the shape a renamed gate takes when it '
    + 'silently disables a feature. Open it to add the definition.',
  unused: 'Configured here, but absent from FEATURE_GATE_NAMES — no app code reads it. '
    + 'Likely obsolete or renamed; safe to delete once confirmed.',
};

type StatusFilter = 'all' | GateStatus | 'drift';

type SortKey = 'gate' | 'rings' | 'overrides' | 'status' | 'created' | 'modified';
interface SortState { key: SortKey; dir: 'asc' | 'desc'; }

const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All statuses' },
  { value: 'on', label: 'On' },
  { value: 'off', label: 'Off' },
  { value: 'unconfigured', label: 'Not configured' },
  { value: 'unused', label: 'Not used by app' },
  { value: 'drift', label: 'Needs attention (drift)' },
];

const emptyEntry = (): GateEntry => ({
  name: '', enabled: false, description: '', enabledForRings: [], allowOverride: true,
});

/** Grow a textarea to show all its text — a gate's description is read in
 *  full when deciding whether to flip it, not scrolled through a one-line box. */
function fitToContent(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight + 2}px`;
}

export function FeatureGates() {
  const queryClient = useQueryClient();
  const [gates, setGates] = useState<GateEntry[]>([]);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [ringFilter, setRingFilter] = useState<'all' | Ring>('all');
  /** Descriptions are one line by default; clicking one expands just that row —
   *  the rest of the row still opens the edit dialog. */
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<SortState | null>(null);

  /** The gate open in the dialog. `draft` is edited freely and only reaches the
   *  server on Save, so closing the dialog discards. */
  const [draft, setDraft] = useState<GateEntry | null>(null);
  const [draftIsNew, setDraftIsNew] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['featureGates'],
    queryFn: adminApi.getFeatureGates,
  });

  // Only meaningful for a gate that already exists — a new/unsaved draft has
  // no users to have overridden it yet.
  const overridesQuery = useQuery({
    queryKey: ['featureGateOverrides', draft?.name],
    queryFn: () => adminApi.getFeatureGateOverrides(draft!.name.trim()),
    enabled: !!draft && !draftIsNew && !!draft.name.trim(),
  });

  useEffect(() => {
    if (data?.gates) {
      setGates(Object.entries(data.gates).map(([name, def]) => ({
        name,
        enabled: def.enabled,
        description: def.description || '',
        enabledForRings: (def.enabledForRings || []) as Ring[],
        allowOverride: def.allowOverride !== false,
        createdAt: def.createdAt,
        updatedAt: def.updatedAt,
        updatedBy: def.updatedBy,
      })));
    }
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (gateEntries: GateEntry[]) => {
      const record: Record<string, FeatureGateDefinition> = {};
      for (const g of gateEntries) {
        if (!g.name.trim()) continue;
        // The whole map is sent on every save, so a global gate that still
        // carries rings from before it was classified would be resent verbatim
        // and refused by the server — blocking edits to every other gate on the
        // page. Drop them here instead; they mean nothing for a global gate.
        const global = isGlobalGate(g.name.trim());
        record[g.name.trim()] = {
          enabled: g.enabled,
          ...(g.description ? { description: g.description } : {}),
          ...(!global && g.enabledForRings.length > 0 ? { enabledForRings: g.enabledForRings } : {}),
          ...(global || !g.allowOverride ? { allowOverride: false } : {}),
        };
      }
      return adminApi.updateFeatureGates(record);
    },
    onMutate: () => setSaveStatus('saving'),
    onSuccess: () => {
      setSaveStatus('saved');
      queryClient.invalidateQueries({ queryKey: ['featureGates'] });
      setTimeout(() => setSaveStatus('idle'), 2000);
    },
    onError: (err) => {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 4000);
    },
  });

  /** Persist a whole new list. Every mutation on this page goes through here,
   *  so the page never holds unsaved state the admin has to remember to flush —
   *  which is what a single Save button at the bottom of a long list becomes. */
  const persist = (next: GateEntry[]) => {
    setGates(next);
    saveMutation.mutate(next);
  };

  // ── Rows: configured gates, plus the ones only the code knows about ──
  const rows: GateRow[] = useMemo(() => {
    const configured = gates
      .map((entry): GateRow => ({
        name: entry.name,
        description: entry.description,
        status: !entry.name.trim() || KNOWN_GATE_NAMES.has(entry.name.trim())
          ? (entry.enabled ? 'on' : 'off')
          : 'unused',
        entry,
      }));
    const configuredNames = new Set(gates.map(g => g.name.trim()).filter(Boolean));
    const missing = FEATURE_GATE_NAMES
      .filter(n => !configuredNames.has(n))
      .map((n): GateRow => ({
        name: n,
        description: FEATURE_GATE_DESCRIPTIONS[n] || '',
        status: 'unconfigured',
      }));
    // Drift first: an unconfigured gate is a live bug, and sorting it to the top
    // costs nothing when everything is healthy (there are none).
    const rank = (s: GateStatus) => (s === 'unconfigured' ? 0 : s === 'unused' ? 1 : 2);
    return [...configured, ...missing].sort(
      (a, b) => rank(a.status) - rank(b.status) || a.name.localeCompare(b.name),
    );
  }, [gates]);

  const needle = filter.trim().toLowerCase();
  const filteredRows = rows.filter(r => {
    if (needle && !r.name.toLowerCase().includes(needle) && !r.description.toLowerCase().includes(needle)) return false;
    if (statusFilter === 'drift' && r.status !== 'unconfigured' && r.status !== 'unused') return false;
    if (statusFilter !== 'all' && statusFilter !== 'drift' && r.status !== statusFilter) return false;
    if (ringFilter !== 'all') {
      const rings = r.entry?.enabledForRings ?? [];
      // No rings means every ring, so such a gate matches any ring filter.
      if (rings.length > 0 && !rings.includes(ringFilter)) return false;
    }
    return true;
  });

  // `null` = the built-in default (drift-first, then alphabetical — see
  // `rows` above). Clicking a header overrides it; nothing puts it back
  // short of a reload, same as every other sortable table in the console.
  const visibleRows = useMemo(() => {
    if (!sort) return filteredRows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    // All four statuses, distinctly. The default row order above ranks on the
    // same axis but deliberately collapses `on` and `off` into one bucket — it
    // only cares about floating drift to the top — and borrowing that function
    // here gave every configured gate the same sort value. With 59 gates and 2
    // in drift, clicking Status re-sorted 57 rows against an equal key, so the
    // stable sort returned them in the order they already had and the column
    // read as dead.
    //
    // Drift keeps the low end, so ascending agrees with the default ordering and
    // descending puts the gates that are ON first.
    const rank = (s: GateStatus) => (s === 'unconfigured' ? 0 : s === 'unused' ? 1 : s === 'off' ? 2 : 3);
    const value = (r: GateRow): string | number => {
      switch (sort.key) {
        case 'gate': return r.name.toLowerCase();
        case 'rings': return r.entry?.enabledForRings.length ? r.entry.enabledForRings.join(',') : '';
        case 'overrides': return r.entry?.allowOverride === false ? 0 : 1;
        case 'status': return rank(r.status);
        case 'created': return r.entry?.createdAt ?? '';
        case 'modified': return r.entry?.updatedAt ?? '';
      }
    };
    return [...filteredRows].sort((a, b) => {
      const av = value(a), bv = value(b);
      if (av < bv) return -dir;
      if (av > bv) return dir;
      // Ties break on the name, always ascending. Every column here has heavy
      // ties — 57 gates share a status, most share "all rings" — and without
      // this the tied block keeps whatever order the previous sort left it in,
      // so the same click gives a different answer depending on what was clicked
      // before it.
      return a.name.localeCompare(b.name);
    });
  }, [filteredRows, sort]);

  const toggleSort = (key: SortKey) => {
    setSort(prev => (prev?.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }));
  };

  const sortIndicator = (key: SortKey) => (sort?.key === key ? (sort.dir === 'asc' ? ' ↑' : ' ↓') : '');

  const driftCount = rows.filter(r => r.status === 'unconfigured' || r.status === 'unused').length;

  const toggleExpanded = (e: React.MouseEvent, name: string) => {
    e.stopPropagation();
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const openRow = (row: GateRow) => {
    if (row.entry) {
      setDraft({ ...row.entry, enabledForRings: [...row.entry.enabledForRings] });
      setDraftIsNew(false);
    } else {
      // An unconfigured gate opens pre-filled with its canonical description —
      // the admin is adopting a definition the code already implies, not
      // inventing one.
      setDraft({ ...emptyEntry(), name: row.name, description: row.description });
      setDraftIsNew(true);
    }
  };

  const closeDialog = () => { setDraft(null); setDraftIsNew(false); };

  const saveDraft = () => {
    if (!draft?.name.trim()) return;
    const name = draft.name.trim();
    const without = gates.filter(g => g.name.trim() !== name);
    persist([...without, { ...draft, name }]);
    closeDialog();
  };

  const deleteDraft = () => {
    if (!draft) return;
    if (!confirm(`Delete the "${draft.name}" gate definition?\n\nApp code that reads it will fall back to its default (off).`)) return;
    persist(gates.filter(g => g.name.trim() !== draft.name.trim()));
    closeDialog();
  };

  const patchDraft = (updates: Partial<GateEntry>) => setDraft(d => (d ? { ...d, ...updates } : d));

  return (
    <div className="settings-section">
      <div className="section-header">
        <h2>Feature Gates</h2>
      </div>

      {isLoading ? (
        <div className="loading">Loading feature gates...</div>
      ) : (
        <>
          <div className="fg-toolbar">
            <select
              className="fg-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              {STATUS_FILTERS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
            </select>
            <select
              className="fg-select"
              value={ringFilter}
              onChange={(e) => setRingFilter(e.target.value as 'all' | Ring)}
            >
              <option value="all">All rings</option>
              {ALL_RINGS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
            <input
              type="text"
              className="fg-filter-input"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by name or description…"
            />
            <span className="fg-toolbar-spacer" />
            {driftCount > 0 && (
              <button
                type="button"
                className="fg-drift-pill"
                onClick={() => setStatusFilter('drift')}
                title="Gates that are configured but unread, or read but unconfigured."
              >
                ⚠ {driftCount} need{driftCount === 1 ? 's' : ''} attention
              </button>
            )}
            <span className="fg-count">{visibleRows.length} of {rows.length}</span>
            <button
              className="feature-add-btn"
              onClick={() => { setDraft(emptyEntry()); setDraftIsNew(true); }}
            >
              + Add Gate
            </button>
          </div>

          {visibleRows.length === 0 ? (
            <p className="fg-empty">No gate matches these filters.</p>
          ) : (
            <table className="dt fg-table">
              <thead>
                <tr>
                  <th className="fg-col-gate fg-th-sortable" onClick={() => toggleSort('gate')}>Gate{sortIndicator('gate')}</th>
                  <th className="fg-col-rings fg-th-sortable" onClick={() => toggleSort('rings')}>Rings{sortIndicator('rings')}</th>
                  <th className="fg-col-override fg-th-sortable" onClick={() => toggleSort('overrides')}>Overrides{sortIndicator('overrides')}</th>
                  <th className="fg-col-status fg-th-sortable" onClick={() => toggleSort('status')}>Status{sortIndicator('status')}</th>
                  <th className="fg-col-date fg-th-sortable" onClick={() => toggleSort('created')}>Created{sortIndicator('created')}</th>
                  <th className="fg-col-date fg-th-sortable" onClick={() => toggleSort('modified')}>Modified{sortIndicator('modified')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row, i) => {
                  const expanded = expandedRows.has(row.name);
                  return (
                  <tr
                    // Name is the natural key; the index only covers a blank one,
                    // which the dialog will not save. A random key here would
                    // remount every row on every render.
                    key={row.name || `unnamed-${i}`}
                    className="clickable-row"
                    onClick={() => openRow(row)}
                  >
                    <td className="fg-col-gate">
                      <div className="fg-name">{row.name || <em>(unnamed)</em>}</div>
                      {row.description && (
                        <div
                          className={`fg-desc${expanded ? ' fg-desc-expanded' : ''}`}
                          onClick={(e) => toggleExpanded(e, row.name)}
                          title={expanded ? undefined : row.description}
                        >
                          {row.description}
                        </div>
                      )}
                    </td>
                    <td className="fg-col-rings">
                      {isGlobalGate(row.name)
                        ? <span className="fg-muted" title={GLOBAL_HINT}>n/a</span>
                        : row.status === 'unconfigured'
                        ? <span className="fg-muted">—</span>
                        : (row.entry?.enabledForRings.length
                          ? row.entry.enabledForRings.join(', ')
                          : <span className="fg-muted">all rings</span>)}
                    </td>
                    <td className="fg-col-override">
                      {isGlobalGate(row.name)
                        ? <span className="fg-muted" title={GLOBAL_HINT}>n/a</span>
                        : row.status === 'unconfigured'
                        ? <span className="fg-muted">—</span>
                        : (row.entry?.allowOverride
                          ? <span className="fg-muted">allowed</span>
                          : 'locked')}
                    </td>
                    <td className="fg-col-status">
                      <span className={`fg-status-tag fg-status-${row.status}`} title={STATUS_TITLE[row.status]}>
                        {STATUS_LABEL[row.status]}
                      </span>
                    </td>
                    <td className="fg-col-date">
                      {row.entry?.createdAt
                        ? <span title={formatDateTimePrecise(row.entry.createdAt)}>{formatDateMonthDay(row.entry.createdAt)}</span>
                        : <span className="fg-muted">—</span>}
                    </td>
                    <td className="fg-col-date">
                      {row.entry?.updatedAt
                        ? (
                          <span title={`${formatDateTimePrecise(row.entry.updatedAt)}${row.entry.updatedBy ? ` · by ${row.entry.updatedBy}` : ''}`}>
                            {formatDateMonthDay(row.entry.updatedAt)}
                          </span>
                        )
                        : <span className="fg-muted">—</span>}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          <div className="fg-save-row">
            {saveStatus === 'saving' && <span className="feature-status">Saving…</span>}
            {saveStatus === 'saved' && <span className="status-saved feature-status">Saved</span>}
            {saveStatus === 'error' && <span className="status-error feature-status">{saveError || 'Failed to save'}</span>}
          </div>
        </>
      )}

      <Modal
        isOpen={!!draft}
        onClose={closeDialog}
        title={draftIsNew ? (draft?.name ? `Configure "${draft.name}"` : 'Add gate') : `Edit "${draft?.name}"`}
        size="md"
        footer={
          <div className="fg-dialog-footer">
            {!draftIsNew && (
              <button className="btn-danger fg-dialog-delete" onClick={deleteDraft}>Delete</button>
            )}
            <button className="btn-secondary" onClick={closeDialog}>Cancel</button>
            <button
              className="feature-save-btn"
              onClick={saveDraft}
              disabled={!draft?.name.trim() || saveMutation.isPending}
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        }
      >
        {draft && (
          <div className="fg-dialog">
            <div className="fg-field">
              <label className="feature-field-label">Name (unique key)</label>
              <input
                type="text"
                className="feature-field-input"
                value={draft.name}
                onChange={(e) => patchDraft({ name: e.target.value })}
                placeholder="e.g. reevaluate"
                // The name is the key app code matches on; renaming an existing
                // gate here would orphan the old one rather than move it.
                readOnly={!draftIsNew}
              />
            </div>

            <div className="fg-field">
              <label className="feature-field-label">Description</label>
              <textarea
                className="feature-field-input fg-description-input"
                rows={3}
                ref={fitToContent}
                value={draft.description}
                onChange={(e) => { patchDraft({ description: e.target.value }); fitToContent(e.currentTarget); }}
                placeholder="What this gate controls"
              />
            </div>

            <div className="fg-dialog-toggle">
              <label className="fg-switch">
                <input
                  type="checkbox"
                  checked={draft.enabled}
                  onChange={(e) => patchDraft({ enabled: e.target.checked })}
                />
                <span className="fg-slider" />
              </label>
              <span className={`fg-status ${draft.enabled ? 'fg-on' : 'fg-off'}`}>
                {draft.enabled ? 'ON' : 'OFF'}
              </span>
            </div>

            {isGlobalGate(draft.name) && (
              <p className="fg-dialog-note">{GLOBAL_HINT}</p>
            )}

            {draft.enabled && !isGlobalGate(draft.name) && (
              <div className="fg-field">
                <label className="feature-field-label">
                  Rollout rings
                  <span className="fg-rings-hint">
                    {draft.enabledForRings.length === 0 ? ' (none selected = all rings)' : ''}
                  </span>
                </label>
                <div className="fg-ring-checks">
                  {ALL_RINGS.map((ring) => (
                    <label key={ring} className="fg-ring-label">
                      <input
                        type="checkbox"
                        checked={draft.enabledForRings.includes(ring)}
                        onChange={(e) => patchDraft({
                          enabledForRings: e.target.checked
                            ? [...draft.enabledForRings, ring]
                            : draft.enabledForRings.filter(r => r !== ring),
                        })}
                      />
                      {ring}
                    </label>
                  ))}
                </div>
              </div>
            )}

            {!isGlobalGate(draft.name) && (
              <label className="fg-override-label">
                <input
                  type="checkbox"
                  checked={draft.allowOverride}
                  onChange={(e) => patchDraft({ allowOverride: e.target.checked })}
                />
                Allow per-user overrides
                {!draft.allowOverride && <span className="fg-lock-hint">(locked)</span>}
              </label>
            )}

            {!draftIsNew && (
              <p className="fg-dialog-note">
                {overridesQuery.isLoading
                  ? 'Checking per-user overrides…'
                  : overridesQuery.isError
                  ? 'Could not load override counts.'
                  : overridesQuery.data
                  ? overridesQuery.data.total === 0
                    ? 'No users have overridden this gate.'
                    : `${overridesQuery.data.total} user${overridesQuery.data.total === 1 ? '' : 's'} `
                      + `${overridesQuery.data.total === 1 ? 'has' : 'have'} overridden this gate `
                      + `(${overridesQuery.data.on} on, ${overridesQuery.data.off} off).`
                  : null}
              </p>
            )}

            {draftIsNew && KNOWN_GATE_NAMES.has(draft.name.trim()) && (
              <p className="fg-dialog-note">
                App code already reads this gate. Until it is saved it resolves to its
                default (off).
              </p>
            )}
            {!draftIsNew && !KNOWN_GATE_NAMES.has(draft.name.trim()) && (
              <p className="fg-dialog-note fg-dialog-note-warn">
                No app code reads this gate — it is absent from <code>FEATURE_GATE_NAMES</code>.
                Likely obsolete or renamed.
              </p>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
